/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { v4 } from 'uuid';
import { templateRender } from 'boost-template-engine';

import { combineWidgets } from 'src/helpers/ssr-helper';
import { getShop } from 'src/helpers';
import { waitFor } from 'src/helpers/time-helper';
import { DEVICE } from 'src/constants/theme-settings';
import { MAPPING_AVAILABLE_TEMPLATE } from 'src/constants/shopify-integration';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import * as ProductActions from 'src/stores/actions/product-actions';
import * as ShopifyIntegrationSettingsAction from 'src/stores/actions/shopify-integration/shopify-integration-settings-actions';
import * as ThemeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import BgCollectionHeader from 'src/assets/images/shopify-integration-settings/bg-header.png';

type PropsFromState = {
  instantWidget: anu,
  widgetSettings: any,
  scssContent: string
};

type PropsFromDispatch = {};

type ComposedProps = PropsFromState & PropsFromDispatch & WithI18nProps;

class PreviewVisualEditor extends Component<ComposedProps, State> {
  constructor(props) {
    super(props);
    this.state = {
      previewHtml: '',
      loadingRender: false
    };
    this.initializedPreview = false;
    this.widgetSettings = props.widgetSettings || {};
    this.compiledSettingsCss = props.compiledSettingsCss;
    this.previewData = props.previewData;
  }

  componentDidMount() {
    const { getPreviewProducts, widgetSettings, instantWidget } = this.props;
    getPreviewProducts({ size: 5 });

    const iframe = document.getElementById('preview-shopify-integration-setting');
    this.iframeRef = iframe;

    // init preview when settings & instance template ready
    if (!this.initializedPreview && !_.isEmpty(widgetSettings) && !_.isEmpty(instantWidget)) {
      this.initializedPreview = true;
      this.getPreviewData(this.props);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.developerMode === true && this.props.developerMode === nextProps.developerMode) {
      return false;
    }
    return true;
  }

  componentWillReceiveProps(nextProps) {
    const { device, selectedPage } = this.props;
    const { device: nextDevice, selectedPage: nextSelectedPage } = nextProps;

    if (
      // Switch device between desktop + fullscreen and mobile need to refresh
      device !== nextDevice
    ) {
      this.handleRefreshPreview(nextProps);
    }

    if (
      (this.props.developerMode && !nextProps.developerMode) ||
      selectedPage !== nextSelectedPage
    ) {
      this.handleRefreshPreview(nextProps);
    }
  }

  componentWillUnmount() {
    const iframeDocument =
      this.iframeRef?.contentDocument || this.iframeRef?.contentWindow?.document;
    iframeDocument.removeEventListener('click', this.handleClickIframe, false);
  }

  handleLoadIframe = () => {
    const iframeDocument =
      this.iframeRef?.contentDocument || this.iframeRef?.contentWindow?.document;
    iframeDocument.addEventListener('click', this.handleClickIframe, false);

    // const style = document.createElement('style', { type: 'text/css' });
    // style.innerHTML = `
    //   .boost-sd-layout {
    //     background-color: #fff;
    //   }
    // `;

    // iframeDocument.head.appendChild(style);
  };

  handleClickIframe = () => {
    Emitter.emit(EVENT_KEY.CLICK_IFRAME_VISUAL_EDITOR);
  };

  handleRefreshPreview = props => {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.onLoading();

    this.timeout = setTimeout(() => {
      this.compileMainTemplate(props || this.props);
    }, 1000);
  };

  /**
   * compile & render preview template
   */
  compileMainTemplate = async props => {
    const { instantWidget, selectedPage, widgetSettings, translations } = props || this.props;

    const mainWidgetId = _.get(
      instantWidget,
      `widgetIds.${[MAPPING_AVAILABLE_TEMPLATE[selectedPage?.value]]}`
    );
    const mainWidget = instantWidget.widgets[mainWidgetId];
    // instantWidget.widgets[mainWidgetId] || Object.values(instantWidget.widgets)[0];

    const allWidgets = Object.values(instantWidget.widgets).reduce((cur, next) => {
      return [...cur, ...next];
    }, []);

    if (
      !_.isEmpty(mainWidget) &&
      !_.isEmpty(allWidgets) &&
      !_.isEmpty(this.widgetSettings) &&
      !_.isEmpty(this.compiledSettingsCss) &&
      !_.isEmpty(this.previewData)
    ) {
      const compiledTemplate = await combineWidgets(
        mainWidget[0]?.widgetName, // first element is the root
        allWidgets,
        this.widgetSettings,
        this.compiledSettingsCss,
        instantWidget
      );
      const renderedTemplate = templateRender(compiledTemplate, {
        ...this.previewData,
        title: 'Products',
        description: 'Give customers details about the banner image(s) or content on the template.',
        products_count: this.previewData?.total_product || 100,
        image: {
          src: BgCollectionHeader
        },
        swatch_settings: _.get(widgetSettings, 'filterSettings.swatch_settings', {}),
        request: this.getRequestParams(props),
        translations
      });
      waitFor(100);
      this.setState({
        previewHtml: renderedTemplate
      });
    }
    this.offLoading();
  };

  getRequestParams = props => {
    const { selectedPage, device, widgetSettings } = props || this.props;
    const limitPerPage = _.get(widgetSettings, `themeSettings.productList.productsPerPage`, 16);
    const productsPerRowOnDesktop = _.get(
      widgetSettings,
      `themeSettings.productList.productsPerRowOnDesktop`,
      3
    );
    const productsPerRowOnMobile = _.get(
      widgetSettings,
      `themeSettings.productList.productsPerRowOnMobile`,
      2
    );

    const data = {
      t: Date.now(),
      sid: v4(),
      _: 'pf',
      shop: getShop(),
      page: 1,
      sort: 'Relevance',
      locale: 'en',
      event_type: 'init',
      build_filter_tree: 'true',
      handle: 'all',
      previewMode: true,
      pg: selectedPage?.value,
      limit: limitPerPage,
      viewAs: `grid--${
        [DEVICE.DESKTOP, DEVICE.FULLSCREEN].includes(device)
          ? productsPerRowOnDesktop
          : productsPerRowOnMobile
      }`
    };

    if (device === DEVICE.MOBILE) {
      data.device = 'mobile, tablet_portrait_max';
      data.sort = 'relevance';
    }
    return data;
  };

  fetchPreviewData = async () => {
    const host = process.env.REACT_APP_FILTER_URL || 'https://staging.bc-solutions.net';
    const filterUrl = host + '/bc-sf-filter/filter';
    const params = this.getRequestParams();
    const response = await fetch(`${filterUrl}?${new URLSearchParams(params)}`, {
      method: 'GET'
    });
    const previewData = await response.json();
    return previewData;
  };

  getPreviewData = async props => {
    this.onLoading();
    const { previewData, updateFieldReducer } = props || this.props;
    if (_.isEmpty(previewData)) {
      const previewData = await this.fetchPreviewData();
      updateFieldReducer('previewData', previewData);
      this.previewData = previewData;
    }
    this.compileMainTemplate();
  };

  drill = obj => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const buildValue = value => {
        if (!value) return `'${key}': inherit,`;
        if (value && typeof value === 'object') return `'${key}': (${this.drill(value)}),`;
        return `'${key}': ${JSON.stringify(String(value))},`;
      };
      acc += buildValue(value);
      return acc;
    }, '');
  };

  onLoading = () => {
    if (!this.loading) {
      Emitter.emit(EVENT_KEY.LOADING_PREVIEW_THEME_SETTING);
      this.loading = true;
    }
  };

  offLoading = () => {
    if (this.loading) {
      this.loading = false;
      Emitter.emit(EVENT_KEY.COMPLETE_PREVIEW_THEME_SETTING);
    }
  };

  render() {
    const {
      instantWidget,
      widgetSettings,
      scssContent,
      // labelTranslation,
      // previewProductData,
      // previewData,
      developerMode
      // themeNameLib,
      // themeLibVersion
    } = this.props;
    const { previewHtml } = this.state;

    const previewClass = classnames('visual-editor-preview full', {});

    if (_.isEmpty(instantWidget) || _.isEmpty(widgetSettings) || _.isEmpty(scssContent)) {
      return null;
    }

    return (
      <div className={previewClass} style={{ display: !developerMode ? 'block' : 'none' }}>
        {/* <div dangerouslySetInnerHTML={{ __html: previewHtml }} /> */}
        <iframe
          id="preview-shopify-integration-setting"
          title="Shopify integration setting preview"
          onLoad={this.handleLoadIframe}
          srcDoc={`<div class="boost-sd__preview-block">${previewHtml}</div>`}></iframe>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getPreviewProducts: (value, i18n) => dispatch(ProductActions.getPreviewProducts(value, i18n)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateField(fieldName, data)),
  updateFieldThemeIntegrationReducer: (fieldName, data) =>
    dispatch(ThemeIntegrationAction.handleUpdateField(fieldName, data))
});

const mapStateToProps = (state, props) => {
  const widgetSettings = _.get(state, 'themeIntegration.widgetSettings', {});
  const scssContent = _.get(state, 'themeIntegration.scssContent', '');
  const translations = _.get(state, 'themeIntegration.translations', {});

  const isExpandFullscreen = props.prevDevice === DEVICE.FULLSCREEN;

  const previewProductData = {
    shop: _.get(state, 'product.shop', {}),
    products: _.get(state, 'product.products', [])
  };

  return {
    instantWidget: _.get(state, 'templateInstance.templateInstancesPreview', {}),
    widgetSettings,
    scssContent,
    translations,
    isExpandFullscreen,
    themeName: _.get(state, 'shopifyIntegrationSettings.themeName', ''),
    selectedPage: _.get(state, 'shopifyIntegrationSettings.selectedPage', ''),
    device: _.get(state, 'shopifyIntegrationSettings.device'),
    labelTranslation: _.get(state, 'shopifyIntegrationSettings.labelTranslation', {}),
    previewProductData,
    previewData: _.get(state, 'shopifyIntegrationSettings.previewData', {}),
    developerMode: _.get(state, 'shopifyIntegrationSettings.developerMode', false),
    themeNameLib: _.get(state, 'templateInstance.templateInstance.themeNameLib', ''),
    themeLibVersion: _.get(state, 'templateInstance.templateInstance.themeLibVersion', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PreviewVisualEditor));
